import {
  EyewearProtectionParams,
  InjectPrescriptionInfoParams,
  InjectRegistrationModuleParams,
  VirtualMirrorParams,
  WcsExposedRayBanApi,
} from '@/scripts/exposed-api/types/exposed-api'
import RbFeaturedProductsCarousel from '@/elements/pdp/featured-products-carousel/rb-featured-products-carousel.vue'
import RbRoxPrescription from '@/elements/prescription/rb-rox-prescription.vue'
import RbScarcityBar from '@/elements/scarcity-bar/scarcity-bar.vue'
import { StoreInformation } from './types/store-information'
import { StoreInformationMapper } from './StoreInformation'
import { WcsPromotion } from '../interfaces/normalizer'
import { ACTION_LOAD_MINICART } from '@/scripts/store/actions'
import { WcsPromotions } from '../libs/wcs/promotions'
import VirtualMirror from '@/scripts/libs/virtual-mirror/VirtualMirror'
import Vue from 'vue'
import { AlgoliaPlp } from '@/scripts/libs/algolia/plp'
import RbPayOverTime from '@/elements/pay-over-time/rb-pay-over-time.vue'
import RbKlarnaExpress from '@/elements/klarna-express/rb-klarna-express.vue'
import RbPaymentMessageBox from '@/elements/cart/payment-message-box/payment-message-box-wrapper.vue'
import RbEyewearProtection from '@/elements/cart/eyewear-protection/rb-eyewear-protection.vue'
import wcs from '@/scripts/libs/wcs'
import i18n from '../libs/i18n'
import { WCS_HIDE } from '@/scripts/constants/css-classes'

window.ExposedRayBanApi = {} as WcsExposedRayBanApi
export const registerExposedMethods = (vuexStore: any) => {
  registerTilesCarouselInjectionMethod(vuexStore)
  registerExposedStoreInformation(vuexStore)
  registerInitPrescriptionComponent(vuexStore)
  registerInitScarcityComponent(vuexStore)
  registerExposedPricesMethod()
  registerRegistrationModule(vuexStore)
  registerVirtualMirror(vuexStore)
  registerAlgoliaSearch()
  registerInjectPayOverTimeComponent(vuexStore)
  registerInjectKlarnaExpressComponent(vuexStore)
  registerInjectPaymentMessageBoxComponent(vuexStore)
  registerEyewearProtectionComponent(vuexStore)
  registerUpdateEPPTotalPriceInOrderSummaryMobile()
}

const registerRegistrationModule = (vuexStore: any) => {
  const injectRegistrationModule = async (params: InjectRegistrationModuleParams) => {
    const { excerpt, moduleNumber, selector, title } = params

    const moduleComponent = await getRegistrationModuleComponent(moduleNumber)
    const wrapper = selector ? document.querySelector(selector) : null

    if (!moduleComponent || !wrapper) {
      return false
    }

    const RegistrationModuleConstructor = Vue.extend(moduleComponent.default)
    const instance = new RegistrationModuleConstructor({
      store: vuexStore,
      propsData: {
        successTitle: title,
        successSubtitle: excerpt,
      },
    })

    instance.$mount()
    wrapper.appendChild(instance.$el)

    return true
  }

  window.ExposedRayBanApi.injectRegistrationModule = injectRegistrationModule
}

const getRegistrationModuleComponent = async (moduleNumber: number) => {
  switch (moduleNumber) {
    case 1:
      return await import('@/elements/cms-registration-form/cms-registration-module-1.lazy.vue')
    case 2:
      return await import('@/elements/cms-registration-form/cms-registration-module-2.lazy.vue')
    case 3:
      return await import('@/elements/cms-registration-form/cms-registration-module-3.lazy.vue')
    case 4:
      return await import('@/elements/cms-registration-form/cms-registration-module-4.lazy.vue')
    case 5:
      return await import('@/elements/cms-registration-form/cms-registration-module-5.lazy.vue')
    case 6:
      return await import('@/elements/cms-registration-form/cms-registration-module-6.lazy.vue')
    case 7:
      return await import('@/elements/cms-registration-form/cms-registration-module-7.lazy.vue')
    case 8:
      return await import('@/elements/cms-registration-form/cms-registration-module-8.lazy.vue')
    default:
      return undefined
  }
}

const registerVirtualMirror = (vuexStore: any) => {
  const injectVirtualMirror = (params: VirtualMirrorParams) => {
    VirtualMirror.injectAndInit({
      partNumbers: params.partNumbers,
      addToCartText: params.addToCartText,
      target: params.target,
      vuexStore,
    })
  }

  window.ExposedRayBanApi.injectVirtualMirror = injectVirtualMirror

  window.externalInvocationMethodMapping.initVirtualMirror = injectVirtualMirror
}

const registerExposedPricesMethod = () => {
  window.ExposedRayBanApi.getPromotionInProgress = (promotions: WcsPromotion[]) => {
    return WcsPromotions.calculatePromotionInProgress(promotions)
  }
}

const registerTilesCarouselInjectionMethod = (vuexStore: any) => {
  window.ExposedRayBanApi.injectRelatedCarousel = (params) => {
    const { catEntries, selector, customId, callback } = params
    const element = document.querySelector(selector)

    const PdpRelated = Vue.extend(RbFeaturedProductsCarousel)
    const instance = new PdpRelated({
      store: vuexStore,
      propsData: { catalogEntries: catEntries.join(','), id: customId, enableSwiperArrows: true },
    })

    instance.$mount()
    element?.appendChild(instance.$el)
    if (callback) {
      callback()
    }
  }
}

const registerExposedStoreInformation = (vuexStore: any) => {
  window.ExposedRayBanApi.getStoreInformation = (): StoreInformation => {
    const selectedProduct = vuexStore?.getters['pdp/selectedProduct']
    const productType = retrieveProductType(selectedProduct)
    const userData = vuexStore.state.user
    const cartData = vuexStore.state.sidebar.cart
    const storeInformation = StoreInformationMapper.getStoreInformation(
      productType,
      userData,
      cartData
    )
    return storeInformation
  }
}

const retrieveProductType = (selectedProduct: any) => {
  if (selectedProduct?.type) {
    return selectedProduct?.type
  }
  const productTypeFromDom = document.getElementById('producttype')
  return productTypeFromDom
}

const registerInitPrescriptionComponent = (vuexStore: any) => {
  window.ExposedRayBanApi.injectPrescriptionInfo = (
    injectPrescriptionInfoParams: InjectPrescriptionInfoParams
  ) => {
    const {
      buttonLabel,
      isButtonIcon,
      isInfoMessage,
      isModalEnabled,
      modalSubtitle,
      modalTitle,
      orderId,
      orderItemId,
      withModalPortal,
      wrapperNode,
    } = injectPrescriptionInfoParams

    const i18nInstance = i18n.setupLocalization()
    const PrescriptionConstructor = (Vue as any).extend(RbRoxPrescription)
    const ROXinstance = new PrescriptionConstructor({
      store: vuexStore,
      i18n: i18nInstance,
      propsData: {
        buttonLabel,
        isButtonIcon,
        isInfoMessage,
        isModalEnabled,
        orderId,
        orderItemId,
        withModalPortal,
      },
    })

    ROXinstance.$slots.title = [ROXinstance.$createElement('span', modalTitle)]
    ROXinstance.$slots.subtitle = [ROXinstance.$createElement('span', modalSubtitle)]

    ROXinstance.$mount()
    wrapperNode?.appendChild(ROXinstance.$el)
  }
}

const registerInitScarcityComponent = (vuexStore: any) => {
  window.ExposedRayBanApi.injectScarcityComponent = async (params) => {
    const { nodeItem, partNumber, catEntries, orderItemId, loadCart } = params
    if (loadCart) {
      await vuexStore.dispatch(ACTION_LOAD_MINICART)
    }
    const RbScarcityBarConstructor = Vue.extend(RbScarcityBar)
    const RbScarcityBarinstance = new RbScarcityBarConstructor({
      store: vuexStore,
      propsData: {
        partNumber,
        catEntries,
        orderItemId,
      },
    })

    RbScarcityBarinstance.$mount()
    nodeItem?.appendChild(RbScarcityBarinstance.$el)
  }
}

const registerAlgoliaSearch = () => {
  window.ExposedRayBanApi.algoliaSearchByContext = async (ruleContext: string) => {
    const algoliaPlp = new AlgoliaPlp(ruleContext)
    const catalog = await algoliaPlp.fetchCatalog({ appliedFacets: [], facetUrl: '', ruleContext })
    return catalog
  }
}

const registerInjectPayOverTimeComponent = (vuexStore: any) => {
  window.ExposedRayBanApi.injectPayOverTimeComponent = (selector: string) => {
    const element = document.querySelector(selector)
    if (element?.firstElementChild) {
      element?.removeChild(element.firstElementChild)
    }
    const i18nInstance = i18n.setupLocalization()
    const RbPayOverTimeConstructor = Vue.extend(RbPayOverTime)
    const RbPayOverTimeInstance = new RbPayOverTimeConstructor({
      i18n: i18nInstance,
      store: vuexStore,
    })
    RbPayOverTimeInstance.$mount()
    element?.appendChild(RbPayOverTimeInstance.$el)
  }
}

const registerInjectKlarnaExpressComponent = (vuexStore: any) => {
  window.ExposedRayBanApi.injectKlarnaExpressComponent = (selector: string) => {
    const element = document.querySelector(selector)
    if (element?.firstElementChild) {
      element?.removeChild(element.firstElementChild)
    }
    const i18nInstance = i18n.setupLocalization()
    const RbKlarnaExpressConstructor = Vue.extend(RbKlarnaExpress)
    const RbKlarnaExpressInstance = new RbKlarnaExpressConstructor({
      i18n: i18nInstance,
      store: vuexStore,
    })
    RbKlarnaExpressInstance.$mount()
    element?.appendChild(RbKlarnaExpressInstance.$el)
  }
}

const registerInjectPaymentMessageBoxComponent = (vuexStore: any) => {
  window.ExposedRayBanApi.injectPaymentMessageBoxComponent = (selector: string) => {
    const element = document.querySelector(selector)
    if (element?.firstElementChild) {
      element?.removeChild(element.firstElementChild)
    }

    const i18nInstance = i18n.setupLocalization()
    const RbPaymentMessageBoxConstructor = Vue.extend(RbPaymentMessageBox)
    const RbPaymentMessageBoxInstance = new RbPaymentMessageBoxConstructor({
      i18n: i18nInstance,
      store: vuexStore,
    })
    RbPaymentMessageBoxInstance.$mount()
    element?.appendChild(RbPaymentMessageBoxInstance.$el)
  }
}

const registerEyewearProtectionComponent = (vuexStore: any) => {
  window.ExposedRayBanApi.injectEyewearProtectionComponent = async (
    params: EyewearProtectionParams
  ) => {
    const { eyewearProtectionWrapperSelector, itemCountSelector, orderItemId } = params

    const element = document.querySelector(itemCountSelector)
    const eyewearProtectionWrapper = element?.querySelector(eyewearProtectionWrapperSelector)

    if (eyewearProtectionWrapper?.firstChild) {
      eyewearProtectionWrapper.removeChild(eyewearProtectionWrapper.firstChild)
    }

    const i18nInstance = i18n.setupLocalization()
    const RbEyewearProtectionConstructor = Vue.extend(RbEyewearProtection)

    const RbEyewearProtectionInstance = new RbEyewearProtectionConstructor({
      i18n: i18nInstance,
      store: vuexStore,
      propsData: {
        orderItemId: orderItemId,
      },
    })

    RbEyewearProtectionInstance.$mount()
    eyewearProtectionWrapper?.appendChild(RbEyewearProtectionInstance.$el)
  }
}
const registerUpdateEPPTotalPriceInOrderSummaryMobile = () => {
  window.ExposedRayBanApi.updateEPPTotalPriceInOrderSummaryMobile = () => {
    const { currencySymbol, eyewearProtection } = wcs.getStoreConfig()
    const isEyewearProtectionEnabled = eyewearProtection.isEnabled

    const store = document.getElementById('store') as HTMLInputElement
    const totalOrderEppPriceKey = store ? `rayban.${store.value}.totalOrderEppPrice` : ''
    const totalOrderEppPrice = sessionStorage.getItem(totalOrderEppPriceKey)

    const wrapper = document.getElementById('eppRowInOrderSummaryCart')
    const totalOrderEppPriceElement = wrapper?.getElementsByClassName('right')[0]

    if (
      !totalOrderEppPriceElement ||
      !wrapper ||
      !totalOrderEppPrice ||
      !isEyewearProtectionEnabled
    ) {
      return
    }
    totalOrderEppPriceElement.innerHTML = `${currencySymbol}${totalOrderEppPrice}`

    const totalOrderEppPriceAsNumber = Number(totalOrderEppPrice) || 0
    const isWrapperHidden = wrapper.classList.contains(WCS_HIDE)

    if (totalOrderEppPriceAsNumber && isWrapperHidden) {
      wrapper.classList.remove(WCS_HIDE)
    }

    if (!totalOrderEppPriceAsNumber && !isWrapperHidden) {
      wrapper.classList.add(WCS_HIDE)
    }
  }
}
